import { ConsultAdditionalTemplateDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import { executeQuery } from 'app/api/client/graphqlClient'
import { getConsultAdditionalTemplateDtoQuery } from 'app/graphql/customQueries'
import { isNotDeleted } from './softDelete'

/**
 * Repository interface for managing additional template data.
 * Provides read operations for ConsultAdditionalTemplateDto records.
 */
export interface AdditionalTemplateRepository {
    /**
     * Get additional templates for a consult
     * @param consultId The consult ID
     * @returns The additional template data or null if not found
     */
    getByConsultId(
        consultId: string
    ): Promise<ConsultAdditionalTemplateDto | null>

    /**
     * Get additional templates for multiple consults in a single batch query
     * @param consultIds Array of consult IDs
     * @returns Map of consultId to additional template data
     */
    getBatchByConsultIds(
        consultIds: string[]
    ): Promise<Map<string, ConsultAdditionalTemplateDto>>
}

/**
 * GraphQL implementation of AdditionalTemplateRepository.
 * Manages additional template data via direct GraphQL API calls.
 */
export class DataStoreAdditionalTemplateRepository
    implements AdditionalTemplateRepository
{
    /**
     * Get additional templates for multiple consults using individual GraphQL queries per consult ID.
     * @param consultIds Array of consult IDs
     * @returns Map of consultId to additional template data
     */
    async getBatchByConsultIds(
        consultIds: string[]
    ): Promise<Map<string, ConsultAdditionalTemplateDto>> {
        try {
            const templateMap = new Map<string, ConsultAdditionalTemplateDto>()

            const results = await Promise.all(
                consultIds.map(async (consultId) => {
                    const result =
                        await executeQuery<ConsultAdditionalTemplateDto | null>(
                            getConsultAdditionalTemplateDtoQuery,
                            { id: consultId },
                            'getConsultAdditionalTemplateDto'
                        )
                    return { consultId, result }
                })
            )

            results.forEach(({ consultId, result }) => {
                if (result && isNotDeleted(result)) {
                    templateMap.set(consultId, result)
                }
            })

            logger.debug('Batch loaded additional templates for consults', {
                action: 'batch_get_additional_templates',
                metadata: {
                    consultCount: consultIds.length,
                    additionalTemplatesFoundCount: templateMap.size,
                },
            })

            return templateMap
        } catch (error) {
            logger.error(
                'Failed to batch fetch additional templates',
                error as Error,
                {
                    action: 'batch_get_additional_templates_error',
                    metadata: { consultIds },
                }
            )
            throw error
        }
    }

    async getByConsultId(
        consultId: string
    ): Promise<ConsultAdditionalTemplateDto | null> {
        try {
            logger.debug('Querying additional templates by consultId', {
                action: 'get_additional_templates',
                metadata: { consultId },
            })

            // Query by primary key (id = consultId for 1:1 relationship)
            const result =
                await executeQuery<ConsultAdditionalTemplateDto | null>(
                    getConsultAdditionalTemplateDtoQuery,
                    { id: consultId },
                    'getConsultAdditionalTemplateDto'
                )

            if (result && isNotDeleted(result)) {
                logger.debug('Additional templates found', {
                    action: 'get_additional_templates_success',
                    metadata: {
                        consultId,
                        recordId: result.id,
                        templateCount: result.additionalTemplates?.length || 0,
                    },
                })
                return result
            }

            logger.debug('No additional templates found for consult', {
                action: 'get_additional_templates_not_found',
                metadata: { consultId },
            })
            return null
        } catch (error) {
            logger.error(
                'Failed to fetch additional templates',
                error as Error,
                {
                    action: 'get_additional_templates_error',
                    metadata: { consultId },
                }
            )
            throw error
        }
    }
}

// Singleton instance
export const additionalTemplateRepository =
    new DataStoreAdditionalTemplateRepository()
